// useData hook for FilterDrawer

import * as React from 'react'
import { useSelector } from 'react-redux'
import useMediaQuery from '@mui/material/useMediaQuery'
import {
  IUserPreferenceGeneralListItemType,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { selectSearchAreaResult } from '@user/store/map'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useAppDispatch, useAppSelector } from '@user/store/hooks'
import {
  selectAllKnockStatuses,
  selectKnockStatusesLoaded,
} from '@user/store/knockStatus/slice'
import { getAllKnockStatuses } from '@user/store/knockStatus/api'
import { IKnockStatus } from '@user/store/knockStatus/typings'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'

export function resolveKnockStatuses(
  filteredKnockStatusesStringValue: string,
  knownKnockStatusIdsValue: string,
  knockStatuses: IKnockStatus[],
  knockStatusesLoaded: boolean,
  d2dKnockEnabled: boolean,
): string[] {
  if (
    filteredKnockStatusesStringValue === '' &&
    d2dKnockEnabled &&
    knockStatusesLoaded
  ) {
    return knockStatuses.map((ks) => String(ks.id))
  } else if (filteredKnockStatusesStringValue) {
    // Covers both 'none' (explicit clear) and 'id1_id2_...' (saved selection).
    // savedIds is empty for 'none', so validIds will also be empty, but newIds
    // can still fire — ensuring KS created after an explicit clear are auto-checked.
    const savedIds =
      filteredKnockStatusesStringValue === 'none'
        ? []
        : filteredKnockStatusesStringValue.split('_').filter(Boolean)
    if (knockStatusesLoaded) {
      const knownIds = knownKnockStatusIdsValue.split('_').filter(Boolean)
      const validIds = savedIds.filter((id) =>
        knockStatuses.some((ks) => String(ks.id) === id),
      )
      // Any KS not in knownIds and not in savedIds is treated as new — auto-check it.
      const newIds = knockStatuses
        .filter(
          (ks) =>
            !knownIds.includes(String(ks.id)) &&
            !savedIds.includes(String(ks.id)),
        )
        .map((ks) => String(ks.id))
      return [...validIds, ...newIds]
    } else {
      return savedIds
    }
  } else {
    return []
  }
}

export function useData(show: boolean) {
  const dispatch = useAppDispatch()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const searchAreaResult = useSelector(selectSearchAreaResult)
  const d2dKnockEnabled = useD2DKnockEnabled()
  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()
  const knockStatuses = useAppSelector(selectAllKnockStatuses)
  const knockStatusesLoaded = useAppSelector(selectKnockStatusesLoaded)

  const [selectedTypes, setSelectedTypes] = React.useState<string[]>([])
  const [myRecords, setMyRecords] = React.useState<boolean>(true)
  const [showFilterChips, setShowFilterChips] = React.useState<boolean>(true)
  const [showValidation, setShowValidation] = React.useState(false)
  const [selectedKnockStatuses, setSelectedKnockStatuses] = React.useState<
    string[]
  >([])

  // Track initial filter states
  const [initialSelectedTypes, setInitialSelectedTypes] = React.useState<
    string[]
  >([])
  const [initialMyRecords, setInitialMyRecords] = React.useState<boolean>(true)
  const [initialSelectedKnockStatuses, setInitialSelectedKnockStatuses] =
    React.useState<string[]>([])

  // Load knock statuses when D2D is enabled and the drawer is shown, but only
  // if they haven't been loaded yet to avoid duplicate network requests.
  React.useEffect(() => {
    if (!d2dKnockEnabled || !show || knockStatusesLoaded) return
    dispatch(getAllKnockStatuses())
  }, [d2dKnockEnabled, show, knockStatusesLoaded, dispatch])

  const mapViewFilteredEntityTypesPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ==
      IUserPreferenceGeneralListItemType.MapViewFilteredEntityTypes,
  )
  const mapViewFilteredMyRecordsPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.MapViewFilteredMyRecords,
  )
  const showMapViewFilterChipsPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.ShowMapViewFilterChips,
  )
  const mapViewFilteredKnockStatusesPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ==
      IUserPreferenceGeneralListItemType.MapViewFilteredKnockStatuses,
  )
  const mapViewKnownKnockStatusIdsPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type ==
      IUserPreferenceGeneralListItemType.MapViewKnownKnockStatusIds,
  )

  const filteredRecordsStringValue =
    mapViewFilteredEntityTypesPreference?.value ?? 'draftlead_lead_opportunity'
  const myRecordsOnlyStringValue =
    mapViewFilteredMyRecordsPreference?.value ?? 'true'
  const showFilterChipsStringValue =
    showMapViewFilterChipsPreference?.value ?? 'true'
  const filteredKnockStatusesStringValue =
    mapViewFilteredKnockStatusesPreference?.value ?? ''
  const knownKnockStatusIdsValue =
    mapViewKnownKnockStatusIdsPreference?.value ?? ''

  const isMobile = useMediaQuery('(max-width: 600px)')

  React.useEffect(() => {
    setSelectedTypes(filteredRecordsStringValue.split('_').filter(Boolean))
  }, [filteredRecordsStringValue])

  React.useEffect(() => {
    // FR6: "My Records" is retired in favor of the standalone salesperson
    // filter — always show total counts when the flag is on, and never read
    // or write the old preference. Off-flag behavior is unchanged.
    setMyRecords(
      salesPersonFilterEnabled ? false : myRecordsOnlyStringValue === 'true',
    )
  }, [myRecordsOnlyStringValue, salesPersonFilterEnabled])

  React.useEffect(() => {
    setShowFilterChips(showFilterChipsStringValue === 'true')
  }, [showFilterChipsStringValue])

  React.useEffect(() => {
    setSelectedKnockStatuses(
      resolveKnockStatuses(
        filteredKnockStatusesStringValue,
        knownKnockStatusIdsValue,
        knockStatuses,
        knockStatusesLoaded,
        d2dKnockEnabled,
      ),
    )
  }, [
    filteredKnockStatusesStringValue,
    knownKnockStatusIdsValue,
    show,
    knockStatuses,
    knockStatusesLoaded,
    d2dKnockEnabled,
  ])

  // Set initial filter states when drawer opens
  React.useEffect(() => {
    if (show) {
      setInitialSelectedTypes(
        filteredRecordsStringValue.split('_').filter(Boolean),
      )
      setInitialMyRecords(
        salesPersonFilterEnabled ? false : myRecordsOnlyStringValue === 'true',
      )
      setInitialSelectedKnockStatuses(
        resolveKnockStatuses(
          filteredKnockStatusesStringValue,
          knownKnockStatusIdsValue,
          knockStatuses,
          knockStatusesLoaded,
          d2dKnockEnabled,
        ),
      )
    }
  }, [
    show,
    filteredRecordsStringValue,
    myRecordsOnlyStringValue,
    filteredKnockStatusesStringValue,
    knownKnockStatusIdsValue,
    knockStatuses,
    knockStatusesLoaded,
    d2dKnockEnabled,
    salesPersonFilterEnabled,
  ])

  const position = React.useMemo(() => {
    return {
      left: isMobile ? 0 : '72px',
      top: isMobile ? '64px' : 0,
    }
  }, [isMobile])

  // Compute counts for each record type based on applied filters.
  // searchAreaResult is kept current by both getContactLocationsInArea (low-zoom path)
  // and setSearchAreaResult dispatched by the tile cache after viewport-bbox filtering.
  const recordTypeCounts = React.useMemo(() => {
    if (!searchAreaResult)
      return {
        draftlead: 0,
        lead: 0,
        opportunity: 0,
      }
    if (myRecords) {
      return {
        draftlead: searchAreaResult.myDraftLead || 0,
        lead: searchAreaResult.myLead || 0,
        opportunity: searchAreaResult.myOpportunity || 0,
      }
    }
    return {
      draftlead: searchAreaResult.totalDraftLead || 0,
      lead: searchAreaResult.totalLead || 0,
      opportunity: searchAreaResult.totalOpportunity || 0,
    }
  }, [searchAreaResult, myRecords])

  // Total count is the sum of selected record type counts
  const totalCount = React.useMemo(() => {
    return selectedTypes.reduce(
      (sum, type) => sum + (recordTypeCounts[type] || 0),
      0,
    )
  }, [selectedTypes, recordTypeCounts])

  // Number of actual entity pin markers within the visible viewport.
  // Computed in useTileCache with strict viewport bounds (no epsilon).
  const displayedPinCount = searchAreaResult?.displayedPinCount ?? 0

  return {
    selectedTypes,
    setSelectedTypes,
    myRecords,
    setMyRecords,
    showFilterChips,
    setShowFilterChips,
    showValidation,
    setShowValidation,
    initialSelectedTypes,
    initialMyRecords,
    initialSelectedKnockStatuses,
    position,
    recordTypeCounts,
    totalCount,
    displayedPinCount,
    d2dKnockEnabled,
    knockStatuses,
    selectedKnockStatuses,
    setSelectedKnockStatuses,
  }
}
